import UserbackService from './UserbackService.js';
import { Modes } from '../common.js';
import IntercomService from '@/utils/IntercomService';
import CustomerIOService from '@/utils/customer-io.js';
import { identifySegmentUser, trackPageVisitedSegment } from '@/utils/segmentAnalytics';
import SmartLookService from '@/utils/smartlook';

const EVENTS = {
	BUTTON_CLICKED: 'Button Clicked',
};

const BUTTONS = {
	CREATE_NEW_FEATURE: 'Create New Feature',
};

// import mixpanel from 'mixpanel-browser';

/* Private */

const applyFn = (fn) => {
	// Validates that the service runs only on relevant environments
	// and handles exceptions - we cannot fail because external services\libraries
	try {
		if (process.env.NODE_ENV === Modes.Production) {
			return fn();
		}
	} catch (err) {
		console.error(err);
		return null;
	}
};

const PAGEVIEWS_COOKIE_NAME = 'pageviews';

function getCookie(name) {
	const nameEQ = name + '=';
	const ca = document.cookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === ' ') c = c.substring(1, c.length);
		if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
	}
	return null;
}

function getUrlParams(url) {
	const params = new URL(url).searchParams;
	return {
		use_case: params.get('use_case'),
		campaign_id: params.get('campaign_id'),
		adset_id: params.get('adset_id'),
		ad_id: params.get('ad_id'),
	};
}

function reportSegmentPageViews(user) {
	const pageViews = JSON.parse(getCookie(PAGEVIEWS_COOKIE_NAME) || '[]');

	if (window.analytics && pageViews.length > 0) {
		pageViews.forEach((pageView) => {
			const properties = {
				url: pageView.u,
				search: new URL(pageView.u).search,
				timestamp: pageView.ts,
				path: new URL(pageView.u).pathname,

				workspace_id: user?.profile?.account?.id,
				...getUrlParams(pageView.u),
			};

			if (pageView.r) {
				if (pageView.r.ref) {
					properties.referrer = pageView.r.ref;
				}
			}

			window.analytics.page(pageView.t, properties);
		});

		// Clear the cookie after reporting to avoid duplicate reporting
		document.cookie = PAGEVIEWS_COOKIE_NAME + '=; path=/; domain=.ply.io; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
	}
}

/* Public */

const initAnalytics = () => {};

const identifyUser = (user) => {
	applyFn(() => {
		identifySegmentUser(user.id, {
			first_name: user.first_name,
			last_name: user.last_name,
			email: user.email,
			workspace_id: user?.profile?.account?.id,
		});

		SmartLookService.identifyUser(user);
		CustomerIOService.identifyUser(user);

		reportSegmentPageViews(user);
	});
	IntercomService.init(user);
	UserbackService.init(user);
};

// These are general user props stored at the Analytics provider
// props: object with properties to update. e.g. props = { "Plan": "Premium" }
const updateUserProps = (props) => {
	applyFn(() => {
		window.analytics.identify(props);
	});
};

const trackPageVisited = (routeTo) => {
	// (!) Mixpanel tracks page view by default
	applyFn(() => {
		trackPageVisitedSegment(routeTo);
	});
};

const sendEvent = (eventName, props) => {
	applyFn(() => {
		window.analytics.track(eventName, props);
	});
};

const trackButtonClicked = (buttonName, props = {}) => {
	sendEvent(EVENTS.BUTTON_CLICKED, { buttonName, ...props });
};

const AnalyticsService = {
	initAnalytics,
	updateUserProps,
	identifyUser,
	trackPageVisited,
	sendEvent,
	trackButtonClicked,
	BUTTONS,
};

export default AnalyticsService;
